"use client";

import React, {useEffect, useState} from "react";
import {PageHomeNewSchema} from "@/sanity/pages/PageHomeNewTemplate";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import BackgroundVideo from "@/components/global/utils/BackgroundVideo";
import {Carousel, type CarouselApi, CarouselContent, CarouselItem} from "@/shadcn/ui/carousel";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTACornerButton} from "@/shadcn/ui/corner-button";

import card1Fallback from "@/public/home-new/card1-fallback.png";
import card2Fallback from "@/public/home-new/card2-fallback.png";
import card4Fallback from "@/public/home-new/card4-fallback.png";
import card2 from "@/public/home-new/card2.png";
import card3 from "@/public/home-new/card3.jpg";
import card5 from "@/public/home-new/card5.png";
import card6 from "@/public/home-new/card6.jpg";

const StepBadge = ({ index, className, language }: { index: number; className?: string, language: "en" | "de" }) => {
  return (
    <p className={cn(
      "relative px-[1rem] h-[3.25rem] flex items-center justify-center leading-none font-geistmono uppercase text-[1.75rem] font-medium",
      className
    )}>
      {language === "en" ? `Step ${index + 1}` : `Schritt ${index + 1}`}
    </p>
  );
};

const RichTextContent = ({ item, className }: { item: any; className?: string }) => {
  return item?.text ? (
    <div className={cn(
      "relative richtext prose-headings:!font-geist prose-headings:!font-normal prose-headings:text-[3.5rem] lg:prose-headings:text-[4.25rem] prose-headings:mb-[1rem] prose-p:text-[1.75rem] lg:prose-p:text-[2rem] sm:prose-p:w-[90%]",
      className
    )}>
      <PortableText
        value={item.text}
        components={richTextComponent}
      />
    </div>
  ) : null;
};

const StepChip = ({ stepNumber, showIcon }: { stepNumber: number; showIcon?: boolean }) => {
  return (
    <div className="flex-shrink-0 relative gradient-border-white-2 before:opacity-50 before:rotate-180 h-[4rem] lg:h-[6rem] bg-white/[.16] backdrop-blur-[2rem] rounded-full flex items-center gap-[.5rem] lg:gap-[1rem] pl-[.5rem] lg:pl-[1rem] pr-[1.5rem] lg:pr-[2rem]">
      {showIcon ? (
        <svg className="w-[3rem] h-[3rem] lg:w-[4rem] lg:h-[4rem]" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="32" height="32" rx="16" fill="#D9F9B9"/>
          <path d="M14.5 20L10 15.5L10.707 14.793L14.5 18.5855L21.293 11.793L22 12.5L14.5 20Z" fill="#161616"/>
        </svg>
      ) : (
        <div className="flex-shrink-0 w-[3rem] h-[3rem] lg:w-[4rem] lg:h-[4rem] relative gradient-border-white-2 before:opacity-50 before:rotate-180 rounded-full"/>
      )}
      <p className="mb-0 leading-none text-[1.5rem] lg:text-[2rem] text-white">Step {String(stepNumber).padStart(2, '0')}</p>
    </div>
  );
};

const SlideCard1 = ({ item, index, language }: { item: any; index: number; language: "en" | "de" }) => {
  return (
    <div className="relative w-full h-full flex flex-col items-center bg-[#112628] pt-[3rem] lg:pt-[4rem] px-[2rem] overflow-clip">
      <div className="absolute left-0 bottom-[2rem] lg:bottom-[4rem] w-full h-[min(37rem,40%)] mix-blend-screen opacity-70">
        <BackgroundVideo
          playbackId={"Uniq02XYF02K2csMlRJ4wUYRH6iaDgA9kmhC00dh2jNWnw"}
          fallbackImage={card1Fallback}
        />
      </div>

      <StepBadge
        index={index}
        className="text-[#D9F9B9] bg-[#D9F9B9]/[.12] relative mb-[4rem] lg:mb-[5rem]"
        language={language}
      />

      <RichTextContent
        item={item}
        className="prose-headings:text-white prose-p:text-white/60 relative"
      />
    </div>
  );
};

const SlideCard2 = ({ item, index, language }: { item: any; index: number; language: "en" | "de" }) => {
  return (
    <div className="relative w-full h-full flex flex-col items-center bg-[#D9F9B9] pt-[3rem] lg:pt-[4rem] px-[2rem] overflow-clip">
      <Image
        className="absolute !w-full !h-full top-0 left-0 object-cover pointer-events-none"
        src={card2}
        alt=""
      />

      <BackgroundVideo
        className="opacity-50"
        playbackId={"WWKzOI96tyodqjdvLLlutfhD100QwAhyFb2JwBjGb4500"}
        fallbackImage={card2Fallback}
      />

      <StepBadge
        index={index}
        className="text-[#112628] bg-[#112628]/[.12] relative mb-[4rem] lg:mb-[5rem]"
        language={language}
      />

      <RichTextContent
        item={item}
        className="prose-headings:text-black prose-p:text-black/60 relative"
      />

      <div
        className="absolute left-0 bottom-[2rem] lg:bottom-[4rem] w-full flex flex-col gap-[1rem] overflow-clip"
        style={{
          maskImage: 'linear-gradient(to right, transparent 0%, black 20%, black 90%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 20%, black 90%, transparent 100%)'
        }}
      >
        {[
          { steps: [1, 2, 3, 4, 5], offset: 0, iconIndices: [0, 2, 4] },
          { steps: [5, 6, 7, 8, 9], offset: '7rem', iconIndices: [0, 2] },
          { steps: [9, 10, 11, 12, 13], offset: 0, iconIndices: [0, 2, 4] },
          { steps: [13, 14, 15, 16, 17], offset: '7rem', iconIndices: [1, 3] }
        ].map((row, rowIndex) => (
          <div
            key={rowIndex}
            className="flex justify-center gap-[1rem]"
            style={row.offset ? { transform: `translateX(${row.offset})` } : undefined}
          >
            {row.steps.map((step, stepIndex) => (
              <StepChip
                key={step}
                stepNumber={step}
                showIcon={row.iconIndices.includes(stepIndex)}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const SlideCard3 = ({ item, index, imageSource, language }: { item: any; index: number; imageSource: any; language: "en" | "de" }) => {
  return (
    <div className="relative w-full h-full flex flex-col items-center bg-[#E4E4E4] pt-[3rem] lg:pt-[4rem] px-[2rem]">
      <StepBadge
        index={index}
        className="text-[#112628] bg-[#112628]/[.12] relative mb-[4rem] lg:mb-[5rem]"
        language={language}
      />

      <RichTextContent
        item={item}
        className="prose-headings:text-black prose-p:text-black/60 prose-p:w-[min(40rem,80%)]"
      />

      <Image
        className="absolute aspect-[395/212] object-cover !w-[calc(100%-6rem)] md:!w-[calc(100%-8rem)] left-[3rem] md:left-[4rem] bottom-[2.5rem] lg:bottom-[4rem] rounded-full"
        src={imageSource}
        alt=""
      />
    </div>
  );
};

const SlideCard4 = ({ item, index, language }: { item: any; index: number; language: "en" | "de" }) => {
  return (
    <div className="relative w-full h-full flex flex-col items-center bg-[#112628] pt-[3rem] lg:pt-[4rem] px-[2rem]">
      <BackgroundVideo
        playbackId={"qyU6yCBYAziRSd8V0002RSx2ofcHGUBl6MCbPPMQeTEBQ"}
        fallbackImage={card4Fallback}
      />
      <div className="absolute inset-0 bg-[#112628]/70"/>

      <StepBadge
        index={index}
        language={language}
        className="text-[#D9F9B9] bg-[#D9F9B9]/[.12] mb-[4rem] lg:mb-[5rem]"
      />

      <RichTextContent
        item={item}
        className="prose-headings:text-white prose-p:text-white/60"
      />
    </div>
  );
};

const SlideCard5 = ({ item, index, language }: { item: any; index: number; language: "en" | "de" }) => {
  return (
    <div className="relative w-full h-full flex flex-col items-center bg-[#112628] pt-[3rem] lg:pt-[4rem] px-[2rem]">
      <StepBadge
        index={index}
        language={language}
        className="text-[#D9F9B9] bg-[#D9F9B9]/[.12] mb-[4rem] lg:mb-[5rem]"
      />

      <RichTextContent
        item={item}
        className="prose-headings:text-white prose-p:text-white/60"
      />

      <div className="absolute w-full bottom-[2rem]">
        <div className="relative">
          <Image
            className="!w-full"
            src={card5}
            alt=""
          />

          <div className="absolute left-0 top-0 w-[15rem] h-full bg-gradient-to-r from-[#112628]" />
          <div className="absolute right-0 top-0 w-[15rem] h-full bg-gradient-to-l from-[#112628]" />
        </div>
      </div>
    </div>
  );
};

const SLIDE_COMPONENTS = [
  (item: any, index: number, language: "en" | "de") => <SlideCard1 item={item} index={index} language={language} />,
  (item: any, index: number, language: "en" | "de") => <SlideCard2 item={item} index={index} language={language} />,
  (item: any, index: number, language: "en" | "de") => <SlideCard3 item={item} index={index} imageSource={card3} language={language} />,
  (item: any, index: number, language: "en" | "de") => <SlideCard4 item={item} index={index} language={language} />,
  (item: any, index: number, language: "en" | "de") => <SlideCard5 item={item} index={index} language={language} />,
  (item: any, index: number, language: "en" | "de") => <SlideCard3 item={item} index={index} imageSource={card6} language={language} />,
];

export default function CircleSliderSection({
  data,
  className
}: {
  data: PageHomeNewSchema;
  className?: string;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };

    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  return (
    <section
      className={cn(
        "relative overflow-clip py-[12rem] lg:py-[18rem]",
        className
      )}
      style={{ overflowAnchor: 'none' }}
    >
      <div className="container relative flex flex-col items-center ">
        <Chip
          text={data?.stepsChip?.text}
          image={data?.stepsChip?.icon}
        />

        {data?.stepsTitle && (
          <div className="richtext richtext-center">
            <PortableText
              value={data?.stepsTitle}
              components={richTextComponent}
            />
          </div>
        )}

        {data?.stepsCTA && (
          <CTACornerButton
            className="mt-[3rem]"
            data={data.stepsCTA}
          />
        )}

        {data?.stepsItems && (
          <div className="mt-[4rem] lg:mt-[3rem] flex flex-wrap justify-center gap-[.5rem]">
            {data?.stepsItems.map((item, i) => (
              <button
                key={i}
                onClick={() => api?.scrollTo(i)}
                className={cn(
                  "px-[2rem] py-[1rem] min-h-[5.375rem] flex items-center justify-center cursor-pointer transition-all",
                  current === i
                    ? "bg-[#112628] text-white rounded-full"
                    : "bg-[#EDECEB] text-black/60 hover:bg-[#112628]/10"
                )}
              >
                <p className="mb-0 leading-none text-[2rem]">{item?.shortTitle}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {data?.stepsItems && (
        <div className="relative pt-[3rem] lg:pt-[6rem] mt-[3rem] lg:mt-0">
          <DecorationLine
            className="top-0 left-0 lg:hidden"
            orientation="horizontal"
          />

          <Carousel
            className="w-full"
            setApi={setApi}
            opts={{
              align: "center",
              skipSnaps: false,
              loop: true,
            }}
          >
            <CarouselContent className="w-full">
              {data?.stepsItems?.map((item, index) => {
                const SlideComponent = SLIDE_COMPONENTS[index];

                return (
                  <CarouselItem
                    key={index}
                    className="p-0 pl-[1rem] pr-[1rem] basis-[42rem] lg:basis-[59.375rem] aspect-[475/539] flex flex-col items-start"
                  >
                    {SlideComponent ? SlideComponent(item, index, data?.settings?.language ?? "de") : null}
                  </CarouselItem>
                );
              })}
            </CarouselContent>
          </Carousel>
        </div>
      )}
    </section>
  );
}
